"use client";

import React, { useState } from "react";
import { Wrench, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, ShieldCheck, Droplets, Zap, RotateCcw, Layers, Settings } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
export default function AmericanToilets() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("models");
  const productLines = [{
    id: "champion",
    name: "Champion 4 Series",
    tagline: "Heavy-Duty Flushing Power",
    description: "Engineered with a massive 4-inch piston flush valve and 2-3/8 inch trapway designed to virtually eliminate clogs.",
    commonIssues: "Piston seal wear, chain tension misalignment, handle arm stiffness.",
    valveType: "4-inch Accelerating Flush Valve",
    efficiency: "1.28 - 1.6 GPF"
  }, {
    id: "cadet",
    name: "Cadet 3 / Cadet Pro",
    tagline: "Trade-Standard Reliability",
    description: "Features a 3-inch flush valve and PowerWash rim that scrubs the bowl with pressurized water each cycle.",
    commonIssues: "Chemical degradation of 3-inch flapper, slow fill valve shutoff.",
    valveType: "3-inch Flapper Flush Valve",
    efficiency: "1.28 GPF (High Efficiency)"
  }, {
    id: "vormax",
    name: "VorMax Series",
    tagline: "Dual-Action Scrubbing Flush",
    description: "Dual-injection rim cleans 2x better than conventional toilets by delivering two jets around the bowl perimeter.",
    commonIssues: "Dual silicone seal leaks, flapper timing mismatch, jet port scaling.",
    valveType: "Dual Silicone Seal Tower",
    efficiency: "1.28 GPF"
  }, {
    id: "colony",
    name: "Colony / Standard Lines",
    tagline: "Classic Compact Design",
    description: "Traditional 2-inch flush valve design found widely in residential homes and rental properties.",
    commonIssues: "Worn rubber flapper, float cup sticking, corrosion at brass tank bolts.",
    valveType: "2-inch Standard Flapper",
    efficiency: "1.6 GPF"
  }];
  const troubleshootingSteps = [{
    step: "01",
    title: "Identify the Tank Flush Mechanism",
    details: "Remove the tank lid and check whether your toilet uses a classic 2-inch flapper, a wide 3-inch/4-inch flapper, a Champion piston tower, or a dual-seal VorMax assembly."
  }, {
    step: "02",
    title: "Test for Silent Flapper Leaks",
    details: "Add 5-6 drops of food coloring into the tank. Wait 15 minutes without flushing. If color enters the bowl, the flush seal or flapper seat has deteriorated."
  }, {
    step: "03",
    title: "Calibrate Water Fill Level",
    details: "Inspect the water line inside the tank. It should sit approximately 1/2 inch below the overflow tube. Adjust the fill valve screw to prevent phantom refills."
  }, {
    step: "04",
    title: "Check Base Seal and Flange Bolts",
    details: "If water appears on the floor around the toilet base after flushing, inspect the wax ring and ensure the closet bolts are snug without over-tightening the porcelain."
  }];
  const partsCatalog = [{
    part: "Champion 4 Replacement Seal",
    model: "Champion 4 Series",
    sku: "7301111-0070A",
    leadTime: "In Stock on Service Trucks",
    purpose: "Stops phantom running water in 4-inch piston flush towers"
  }, {
    part: "VorMax Silicone Flapper Pair",
    model: "VorMax Plus / Estate",
    sku: "7381424-100.0070A",
    leadTime: "In Stock on Service Trucks",
    purpose: "Restores dual-chamber seal integrity"
  }, {
    part: "Cadet 3 3-Inch Flapper",
    model: "Cadet 3 & Cadet Pro",
    sku: "7381129-200.0070A",
    leadTime: "In Stock on Service Trucks",
    purpose: "Chemical-resistant rubber seal replacement"
  }, {
    part: "Universal Fluidmaster 400A Fill Valve",
    model: "All American Standard Lines",
    sku: "400ARHR",
    leadTime: "In Stock on Service Trucks",
    purpose: "Fixes slow fills, hissing valves, and continuous running"
  }];
  const faqs = [{
    question: "Why does my American Standard Champion 4 toilet keep running intermittently?",
    answer: "The most common cause is a warped or mineral-encrusted blue silicone seal on the 4-inch flush tower. Because Champion 4 uses a large gasket rather than a traditional flapper, even small micro-cracks will cause continuous water seep and periodic tank refilling."
  }, {
    question: "Can I replace an American Standard flush valve with a generic universal part?",
    answer: "Standard 2-inch models accept universal parts, but Champion 4 (4-inch opening) and VorMax (dual silicone flapper mechanism) require genuine OEM American Standard replacement parts to guarantee proper flush volume and complete seal closure."
  }, {
    question: "What is covered under the American Standard toilet warranty?",
    answer: "American Standard offers lifetime limited warranties on chinaware (porcelain), 10-year limited warranties on Champion 4 mechanical parts, and 5-year warranties on Cadet mechanical components. Our plumbers can help document failures for manufacturer warranty claims."
  }, {
    question: "Why is water pooling around the base of my American Standard toilet?",
    answer: "Water at the base usually indicates a broken wax ring seal, loose closet bolts, a leaking tank-to-bowl gasket, or a cracked porcelain trapway. If you notice pooling, turn off the water supply valve and contact us immediately to prevent subfloor water damage."
  }];
  return <section id="americanstandardtoilets" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs uppercase tracking-wide">
              Brand Specialization
            </Badge>
            <Badge variant="outline" className="px-3 py-1 font-medium text-xs uppercase tracking-wide text-primary border-primary/30">
              OEM Certified Procedures
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            American Standard Toilet Repair & Troubleshooting
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default">
            Complete diagnostic support, parts sourcing, and emergency repair protocols for Champion 4, Cadet 3, VorMax, and classic American Standard fixtures in Jonesboro, GA.
          </p>
        </motion.div>

        {/* Urgent Alert Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="mb-10">
          <Alert className="border-primary/20 bg-secondary/50">
            <AlertTriangle className="size-5 text-primary" />
            <AlertTitle className="text-foreground font-semibold">
              Experiencing an active tank leak or severe overflow in Jonesboro?
            </AlertTitle>
            <AlertDescription className="text-muted-foreground flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-1">
              <span>
                Our licensed plumbers carry OEM American Standard flappers, fill valves, and wax rings 24/7.
              </span>
              <a href="tel:4708479076" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline shrink-0">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </AlertDescription>
          </Alert>
        </motion.div>

        {/* Main Tabbed Information System */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-16">
          <div className="flex justify-center mb-8">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full max-w-3xl h-auto p-1.5 gap-1 bg-muted rounded-xl">
              <TabsTrigger value="models" className="py-2.5 px-3 text-xs sm:text-sm font-medium rounded-lg">
                <Layers className="size-4 mr-1.5 hidden sm:inline" />
                Product Lines
              </TabsTrigger>
              <TabsTrigger value="diagnostics" className="py-2.5 px-3 text-xs sm:text-sm font-medium rounded-lg">
                <Zap className="size-4 mr-1.5 hidden sm:inline" />
                Common Issues
              </TabsTrigger>
              <TabsTrigger value="parts" className="py-2.5 px-3 text-xs sm:text-sm font-medium rounded-lg">
                <Wrench className="size-4 mr-1.5 hidden sm:inline" />
                Parts & Specs
              </TabsTrigger>
              <TabsTrigger value="warranty" className="py-2.5 px-3 text-xs sm:text-sm font-medium rounded-lg">
                <ShieldCheck className="size-4 mr-1.5 hidden sm:inline" />
                Warranty & Care
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: Product Lines */}
          <TabsContent value="models" className="mt-0 outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {productLines.map((model, i) => <div key={model.id} data-index={i}>
                  <Card className="h-full border border-border shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardHeader>
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="outline" className="text-primary border-primary/30">
                          {model.efficiency}
                        </Badge>
                        <span className="text-xs font-mono text-muted-foreground">
                          {model.valveType}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-foreground mt-2">
                        {model.name}
                      </CardTitle>
                      <CardDescription className="text-primary font-medium text-xs sm:text-sm">
                        {model.tagline}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {model.description}
                      </p>
                      <div className="rounded-lg bg-muted/60 p-3 border border-border/50 text-xs">
                        <span className="font-semibold text-foreground block mb-1">
                          Key Diagnostic Focus:
                        </span>
                        <span className="text-muted-foreground">{model.commonIssues}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 2: Common Issues & Step-by-Step Diagnostics */}
          <TabsContent value="diagnostics" className="mt-0 outline-none">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-4">
                <h3 className="text-xl font-bold font-default text-foreground flex items-center gap-2">
                  <RotateCcw className="size-5 text-primary" />
                  Standard Diagnostic Sequence
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Follow these proven steps to trace flushing failures, phantom filling, or seal decay before purchasing replacement parts.
                </p>

                <div className="space-y-3">
                  {troubleshootingSteps.map((item, i) => <div key={item.step} data-index={i} className="p-4 rounded-xl border border-border bg-card flex items-start gap-4 shadow-2xs">
                      <div className="flex items-center justify-center size-9 rounded-lg bg-primary/10 text-primary font-bold text-sm shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground mb-1">
                          {item.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {item.details}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="lg:col-span-5">
                <Card className="border border-border bg-secondary/30">
                  <CardHeader>
                    <CardTitle className="text-lg font-bold font-default text-foreground">
                      Common American Standard Faults
                    </CardTitle>
                    <CardDescription>
                      Typical failure points identified during local Jonesboro service calls.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex items-start gap-2.5 pb-2 border-b border-border">
                      <Droplets className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground text-xs">Phantom Running (Every 10-30 Mins)</p>
                        <p className="text-muted-foreground text-xs">Caused by Champion 4 blue silicone seal wear or Cadet 3 flapper warping.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5 pb-2 border-b border-border">
                      <AlertTriangle className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground text-xs">Weak or Incomplete Siphon</p>
                        <p className="text-muted-foreground text-xs">Caused by practical rim jet scaling, siphon jet blockages, or low tank water line.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Wrench className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground text-xs">Tank-to-Bowl Gasket Leaks</p>
                        <p className="text-muted-foreground text-xs">Rubber bolt grommets corrode due to in-tank drop-in chlorine tablets.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* TAB 3: Parts & Availability Specs */}
          <TabsContent value="parts" className="mt-0 outline-none">
            <Card className="border border-border shadow-xs">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-bold font-default text-foreground">
                      Genuine OEM Replacement Parts Inventory
                    </CardTitle>
                    <CardDescription>
                      All service vehicles carry certified American Standard components to complete same-day repairs.
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="w-fit text-xs">
                    Truck Stock Ready
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Part Name</TableHead>
                      <TableHead>Compatible Series</TableHead>
                      <TableHead className="hidden md:table-cell">OEM SKU</TableHead>
                      <TableHead>Function & Benefit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partsCatalog.map((row, i) => <TableRow key={row.sku} data-index={i}>
                        <TableCell className="font-semibold text-foreground text-xs sm:text-sm">
                          {row.part}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm text-muted-foreground">
                          {row.model}
                        </TableCell>
                        <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">
                          {row.sku}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm text-muted-foreground">
                          {row.purpose}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: Warranty & Maintenance Guidelines */}
          <TabsContent value="warranty" className="mt-0 outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border border-border bg-card">
                <CardHeader>
                  <ShieldCheck className="size-8 text-primary mb-2" />
                  <CardTitle className="text-lg font-bold font-default">Chinaware Warranty</CardTitle>
                  <CardDescription>Limited Lifetime Coverage</CardDescription>
                </CardHeader>
                <CardContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Vitreous china bowls and tanks carry lifetime protection against structural casting defects and crazing under normal residential use.
                </CardContent>
              </Card>

              <Card className="border border-border bg-card">
                <CardHeader>
                  <Settings className="size-8 text-primary mb-2" />
                  <CardTitle className="text-lg font-bold font-default">Mechanical Components</CardTitle>
                  <CardDescription>5 to 10 Year Protection</CardDescription>
                </CardHeader>
                <CardContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Flush valves, fill valves, trip levers, and actuators are covered against manufacturing defects. In-tank chemical tablet use voids seal coverage.
                </CardContent>
              </Card>

              <Card className="border border-border bg-card">
                <CardHeader>
                  <CheckCircle2 className="size-8 text-primary mb-2" />
                  <CardTitle className="text-lg font-bold font-default">Professional Labor Guarantee</CardTitle>
                  <CardDescription>100% Jonesboro Guarantee</CardDescription>
                </CardHeader>
                <CardContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our emergency repairs come backed by our local workmanship warranty, ensuring proper torque, flawless seals, and leak-free performance.
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Accordion FAQ Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold font-default text-foreground mb-2">
              Frequently Asked Troubleshooting Questions
            </h3>
            <p className="text-sm text-muted-foreground">
              Practical answers for homeowners dealing with American Standard toilet issues.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full border rounded-xl bg-card px-4 sm:px-6">
            {faqs.map((faq, i) => <AccordionItem key={faq.question} value={`faq-${i}`} data-index={i}>
                <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary text-sm sm:text-base">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* CTA Footer Card */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left max-w-xl">
              <span className="text-xs uppercase font-semibold tracking-wider text-accent block mb-1">
                24/7 Local Emergency Service
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold font-default text-primary-foreground mb-2">
                Need Fast American Standard Toilet Repair?
              </h3>
              <p className="text-sm sm:text-base text-primary-foreground/90 font-default">
                Jonesboro Emergency Plumber delivers rapid diagnostic response, genuine replacement parts, and upfront pricing across Jonesboro and surrounding areas.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Button variant="secondary" size="lg" asChild className="w-full sm:w-auto font-semibold shadow-xs">
                <a href="tel:4708479076" className="flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild className="w-full sm:w-auto font-semibold bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/toilet-repair/" className="flex items-center gap-2">
                  <span>Toilet Services</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}